import _ from 'lodash';
import { BallType, PassResult } from '../enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from '../enums/pitch-performance';
import {
  IPitchDict,
  IPitchPerformanceFire,
} from '../interfaces/data/i-pitch-performance';
import {
  EMPTY_METRIC_DETAILS,
  EMPTY_SHOT_ACTUALS,
  IFireWithMetrics,
  IGroupedFireDict,
  IGroupedFires,
  IMetricDetails,
  IMetricTally,
  IShotActuals,
} from '../interfaces/i-pitch-performance';
import { IRealMachinePerformance } from '../interfaces/modelling/i-real-machine-metric';
import {
  IBallState,
  IPlateLoc,
  ITrajectory,
  ITrajektRefBreak,
} from '../interfaces/pitches';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { BallHelper } from './ball.helper';
import { EllipseHelper } from './ellipse.helper';
import { FT_TO_INCHES, mean, meanObject } from './math.utilities';
import { getMSFromMSDict } from './ms.helper';
import { TrajHelper } from './trajectory.helper';

export const SKIP_OVERALL_CHECKS = true;

export class MachinePerformanceHelper {
  /** true => model passes, false => fails */
  static checkOverall = (value: IRealMachinePerformance): boolean => {
    if (SKIP_OVERALL_CHECKS) {
      return true;
    }

    return [
      this.checkSpeedY(value) === PassResult.Pass,
      this.checkSpinOverall(value),
      this.checkPlateOverall(value),
    ].every((v) => v === true);
  };

  static checkSpeedY = (value: IRealMachinePerformance): PassResult => {
    const MEAN_AE = 1.4;
    const MAX_AE = 2.8;

    if (value.mean_stdev.vy > MEAN_AE && value.max_stdev.vy > MAX_AE) {
      return PassResult.Both;
    }

    if (value.mean_stdev.vy > MEAN_AE) {
      return PassResult.Mean;
    }

    if (value.max_stdev.vy > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static checkPlateOverall = (value: IRealMachinePerformance): boolean => {
    return [
      this.checkPlateDimension(value.mean_stdev.plx, value.max_stdev.plx),
      this.checkPlateDimension(value.mean_stdev.plz, value.max_stdev.plz),
    ].every((v) => v === PassResult.Pass);
  };

  static checkPlateDimension = (mean: number, max: number): PassResult => {
    // inches
    const MEAN_AE = 5.5;
    const MAX_AE = 10;

    if (mean > MEAN_AE && max > MAX_AE) {
      return PassResult.Both;
    }
    if (mean > MEAN_AE) {
      return PassResult.Mean;
    }

    if (max > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static checkSpinOverall = (value: IRealMachinePerformance): boolean => {
    return [
      this.checkSpinDimension(value.mean_stdev.wx, value.max_stdev.wx),
      this.checkSpinDimension(value.mean_stdev.wy, value.max_stdev.wy),
      this.checkSpinDimension(value.mean_stdev.wz, value.max_stdev.wz),
    ].every((v) => v === PassResult.Pass);
  };

  // clones input before adding averages and deltas to the grouped fires
  static cloneWithAvgAndDelta = (input: IGroupedFires): IGroupedFires => {
    const output = _.cloneDeep(input);

    output.rotatedShots = EllipseHelper.getRotatedShots({
      ms: output.target.ms,
      traj: output.target.traj,
      shots: output.fires.map((f) => f.shot),
      plate_distance: output.target.ms.py,
    });

    const validBss = output.rotatedShots
      .filter((s) => s.bs)
      .map((s) => s.bs as IBallState);

    const validTrajs = output.rotatedShots
      .filter((s) => s.traj)
      .map((s) => s.traj as ITrajectory);

    const speedMPHs: number[] = validBss.map((bs) => BallHelper.getSpeed(bs));

    const netSpins: number[] = validBss.map((bs) => BallHelper.getNetSpin(bs));

    const breaks: ITrajektRefBreak[] = output.rotatedShots.map((s) =>
      TrajHelper.getBreaksFromShot(s)
    );

    const plates: IPlateLoc[] = validTrajs.map((traj) =>
      TrajHelper.getPlateLoc(traj)
    );

    output.average.speedMPH = mean(speedMPHs);
    output.average.netSpin = mean(netSpins);

    const avgBreak = breaks.length > 0 ? meanObject(breaks) : undefined;
    if (avgBreak) {
      output.average.break = avgBreak;
    }

    const avgPlate = plates.length > 0 ? meanObject(plates) : undefined;
    if (avgPlate) {
      output.average.plate = avgPlate;
    }

    output.delta.speedMPH = output.average.speedMPH - output.target.speedMPH;
    output.delta.netSpin = output.average.netSpin - output.target.netSpin;
    output.delta.break = {
      xInches: output.average.break.xInches - output.target.break.xInches,
      zInches: output.average.break.zInches - output.target.break.zInches,
    };
    output.delta.plate = {
      plate_x: output.average.plate.plate_x - output.target.plate.plate_x,
      plate_z: output.average.plate.plate_z - output.target.plate.plate_z,
    };

    return output;
  };

  static checkSpinDimension = (mean: number, max: number): PassResult => {
    const MEAN_AE = 650;
    const MAX_AE = 1_000_000;

    if (mean > MEAN_AE && max > MAX_AE) {
      return PassResult.Both;
    }
    if (mean > MEAN_AE) {
      return PassResult.Mean;
    }

    if (max > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static getGroupsFromFires = (
    fires: IPitchPerformanceFire[]
  ): IGroupedFires[] => {
    // similar colors are sorted together
    // used for picking a "different enough" color for consecutive items (e.g. if pitches are sorted by some similar attribute)
    const RGB_OFFSET = 4;

    // todo: define a bunch of actually good colors
    const RGB_COLORS: string[] = [
      '0,150,136',
      '0,188,212',
      '103,58,183',
      '121,85,72',
      '139,195,74',
      '156,39,176',
      '205,220,57',
      '233,30,99',
      '244,67,54',
      '255,152,0',
      '255,193,7',
      '255,235,59',
      '255,87,34',
      '3,169,244',
      '33,150,243',
      '63,81,181',
      '76,175,80',
      '96,125,139',
    ];

    const dict: IGroupedFireDict = {};

    fires.forEach((f) => {
      if (!f.pitch.id) {
        return;
      }

      if (!f.pitch.name) {
        return;
      }

      if (!f.pitch.type) {
        return;
      }

      const { shot } = f;

      if (!shot) {
        return;
      }

      if (!shot.plate) {
        return;
      }

      if (!shot.target_bs) {
        return;
      }

      if (!shot.target_traj) {
        return;
      }

      if (!dict[f.pitch.id]) {
        // rotate through the available colors
        const counter = Object.keys(dict).length;

        // create an entry with the target on first encounter
        const entry: IGroupedFires = {
          rgb: RGB_COLORS[(counter * RGB_OFFSET) % RGB_COLORS.length],
          _id: f.pitch.id,
          name: f.pitch.name,
          type: f.pitch.type,

          target: {
            ms: shot.target_ms,
            traj: shot.target_traj,

            speedMPH: BallHelper.getSpeed(shot.target_bs),
            netSpin: BallHelper.getNetSpin(shot.target_bs),
            plate: shot.plate,
            break:
              // prefer pitch's breaks, if any, before defaulting to traj's breaks
              shot.target_ms.pitchBreaks ??
              TrajHelper.getBreaks(shot.target_traj),
          },

          // will be computed once all fires are assembled
          average: {
            speedMPH: 0,
            netSpin: 0,
            plate: {
              plate_x: 0,
              plate_z: 0,
            },
            break: {
              xInches: 0,
              zInches: 0,
            },
          },

          // will be computed once all fires are assembled
          delta: {
            speedMPH: 0,
            netSpin: 0,
            plate: {
              plate_x: 0,
              plate_z: 0,
            },
            break: {
              xInches: 0,
              zInches: 0,
            },
          },

          fires: [],

          rotatedShots: [],
        };

        dict[f.pitch.id] = entry;
      }

      dict[f.pitch.id].fires.push(f);
    });

    return Object.values(dict).map((g) => this.cloneWithAvgAndDelta(g));
  };

  static getCountMetric(
    target: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    shot: IMachineShot
  ): IMetricDetails {
    const emptyResult = _.cloneDeep(EMPTY_METRIC_DETAILS);

    if (!shot.bs) {
      return emptyResult;
    }

    if (!shot.target_traj) {
      return emptyResult;
    }

    if (!shot.traj) {
      return emptyResult;
    }

    const {
      speedMPH: actualVelo,
      break: actualBreak,
      plate: actualPlate,
    } = this.getShotActuals(shot);

    const veloOK = Math.abs(target.speedMPH - actualVelo) <= MAX_VELO_DELTA_MPH;

    const MAX_BREAKS_DELTA_INCHES =
      shot.ball_type === BallType.Trajekt_36
        ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
        : MAX_BREAKS_DELTA_IN;

    const breakDistanceIn = Math.sqrt(
      Math.pow(target.break.xInches - actualBreak.xInches, 2) +
        Math.pow(target.break.zInches - actualBreak.zInches, 2)
    );
    const breakOK = breakDistanceIn <= MAX_BREAKS_DELTA_INCHES;

    const plateDistanceFt = Math.sqrt(
      Math.pow(target.plate.plate_x - actualPlate.plate_x, 2) +
        Math.pow(target.plate.plate_z - actualPlate.plate_z, 2)
    );
    const plateOK = plateDistanceFt * FT_TO_INCHES <= MAX_PLATE_DELTA_IN;

    const o: IMetricDetails = {
      total: 1,
      overall: veloOK && breakOK && plateOK ? 1 : 0,
      velocity: veloOK ? 1 : 0,
      break: breakOK ? 1 : 0,
      plate: plateOK ? 1 : 0,
    };

    return o;
  }

  static getTallyFromShotIds = (
    groups: IGroupedFires[],
    shotIds: string[]
  ): IMetricTally => {
    const shots = groups.flatMap((group) =>
      group.rotatedShots.filter((shot) => shotIds.includes(shot._id))
    );

    // calculate the metric for each shot from each group
    const metrics = _.compact(
      shots.flatMap((shot) => {
        if (
          !shot ||
          !shot.bs ||
          !shot.target_traj ||
          !shot.traj ||
          !shot.target_bs ||
          !shot.plate
        ) {
          return;
        }

        const targetSpeedMPH = BallHelper.getSpeed(shot.target_bs);
        const targetBreak = TrajHelper.getBreaks(shot.target_traj);

        const target = {
          speedMPH: targetSpeedMPH,
          plate: shot.plate,
          break: targetBreak,
        };

        return this.getCountMetric(target, shot);
      })
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: _.sumBy(metrics, (m) => m.total),
      overall: _.sumBy(metrics, (m) => m.overall),
      velocity: _.sumBy(metrics, (m) => m.velocity),
      break: _.sumBy(metrics, (m) => m.break),
      plate: _.sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  static getTallyFromGroups = (groups: IGroupedFires[]): IMetricTally => {
    // calculate the metric for each shot from each group
    const metrics = groups.flatMap((group) =>
      group.rotatedShots.flatMap((shot) =>
        this.getCountMetric(group.target, shot)
      )
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: _.sumBy(metrics, (m) => m.total),
      overall: _.sumBy(metrics, (m) => m.overall),
      velocity: _.sumBy(metrics, (m) => m.velocity),
      break: _.sumBy(metrics, (m) => m.break),
      plate: _.sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  // determines if the pitch's average speed, break, and location are within spec
  static getCountAverageMetric(
    target: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    average: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    ball_type?: BallType
  ): IMetricDetails {
    const emptyResult = _.cloneDeep(EMPTY_METRIC_DETAILS);

    const {
      break: averageBreak,
      plate: averagePlate,
      speedMPH: averageVelo,
    } = average;

    if (!averageBreak || !averageVelo || !averagePlate) {
      return emptyResult;
    }

    const veloOK =
      Math.abs(target.speedMPH - averageVelo) <= MAX_VELO_DELTA_MPH;

    const MAX_BREAKS_DELTA_INCHES =
      ball_type === BallType.Trajekt_36
        ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
        : MAX_BREAKS_DELTA_IN;

    const breakDistanceIn = Math.sqrt(
      Math.pow(target.break.xInches - averageBreak.xInches, 2) +
        Math.pow(target.break.zInches - averageBreak.zInches, 2)
    );

    const breakOK = breakDistanceIn <= MAX_BREAKS_DELTA_INCHES;

    const plateDistanceFt = Math.sqrt(
      Math.pow(target.plate.plate_x - averagePlate.plate_x, 2) +
        Math.pow(target.plate.plate_z - averagePlate.plate_z, 2)
    );

    const plateOK = plateDistanceFt * FT_TO_INCHES <= MAX_PLATE_DELTA_IN;

    const o: IMetricDetails = {
      total: 1,
      overall: veloOK && breakOK && plateOK ? 1 : 0,
      velocity: veloOK ? 1 : 0,
      break: breakOK ? 1 : 0,
      plate: plateOK ? 1 : 0,
    };

    return o;
  }

  static getAverageTallyFromGroups = (
    groups: IGroupedFires[]
  ): IMetricTally => {
    const ball_type = groups[0]?.rotatedShots[0]?.ball_type;

    // calculate the metric for the average of each group
    const metrics = groups.flatMap((group) =>
      this.getCountAverageMetric(group.target, group.average, ball_type)
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: _.sumBy(metrics, (m) => m.total),
      overall: _.sumBy(metrics, (m) => m.overall),
      velocity: _.sumBy(metrics, (m) => m.velocity),
      break: _.sumBy(metrics, (m) => m.break),
      plate: _.sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  static getPercentMetric(count: IMetricDetails): IMetricDetails {
    if (count.total === 0) {
      return { ...EMPTY_METRIC_DETAILS };
    }

    const output: IMetricDetails = {
      total: 100,
      overall: 100 * (count.overall / count.total),
      velocity: 100 * (count.velocity / count.total),
      break: 100 * (count.break / count.total),
      plate: 100 * (count.plate / count.total),
    };

    return output;
  }

  // e.g. converts a count good into count bad and vice-versa
  static invertCountMetric(count: IMetricDetails): IMetricDetails {
    const output: IMetricDetails = {
      total: count.total,
      overall: count.total - count.overall,
      velocity: count.total - count.velocity,
      break: count.total - count.break,
      plate: count.total - count.plate,
    };

    return output;
  }

  // e.g. converts a % good into % bad and vice-versa
  static invertPercentMetric(percent: IMetricDetails): IMetricDetails {
    const output: IMetricDetails = {
      total: 100,
      overall: 100 - percent.overall,
      velocity: 100 - percent.velocity,
      break: 100 - percent.break,
      plate: 100 - percent.plate,
    };

    return output;
  }

  static getShotActuals(shot: IMachineShot): IShotActuals {
    const emptyResult = _.cloneDeep(EMPTY_SHOT_ACTUALS);

    if (!shot.bs) {
      return emptyResult;
    }

    if (!shot.target_traj) {
      return emptyResult;
    }

    if (!shot.traj) {
      return emptyResult;
    }

    const actualVelo = BallHelper.getSpeed(shot.bs);
    const actualBreak = TrajHelper.getBreaksFromShot(shot);
    const actualPlate = TrajHelper.getPlateLoc(shot.traj);

    return {
      speedMPH: actualVelo,
      break: actualBreak,
      plate: actualPlate,
    };
  }

  static getFireGroupMetrics(group: IGroupedFires): IFireWithMetrics[] {
    const shots = group.fires.map((s) => s.shot);

    const metrics = shots.map((shot) => {
      if (
        !shot ||
        !shot.bs ||
        !shot.target_traj ||
        !shot.traj ||
        !shot.target_bs ||
        !shot.plate
      ) {
        return;
      }

      const actuals = this.getShotActuals(shot);

      const targetSpeedMPH = BallHelper.getSpeed(shot.target_bs);
      const targetBreak = TrajHelper.getBreaks(shot.target_traj);

      return {
        _id: shot._id,
        speedMPH: actuals.speedMPH,
        plate: actuals.plate,
        break: actuals.break,
        target: {
          ms: shot.target_ms,
          traj: shot.target_traj,

          speedMPH: targetSpeedMPH,
          plate: shot.plate,
          break: targetBreak,
        },
        delta: {
          speedMPH: actuals.speedMPH - targetSpeedMPH,
          break: {
            xInches: actuals.break.xInches - targetBreak.xInches,
            zInches: actuals.break.zInches - targetBreak.zInches,
          },
          plate: {
            plate_x: actuals.plate.plate_x - shot.plate.plate_x,
            plate_z: actuals.plate.plate_z - shot.plate.plate_z,
          },
        },
        shot,
      };
    });

    return _.compact(metrics);
  }

  static getPitchPerformanceFire = (
    shot: IMachineShot,
    pitchDict: IPitchDict,
    // nightly PP records and data API requests care about all shots
    // not just the ones matching the current ms
    checkHash: boolean
  ): IPitchPerformanceFire | undefined => {
    if (!shot.pitch_id) {
      return;
    }

    if (!shot.ball_type) {
      return;
    }

    const pitch = pitchDict[shot.pitch_id];

    if (!pitch) {
      return;
    }

    if (!pitch.type) {
      return;
    }

    const shotTargetMs = shot.target_ms;

    const { ms: pitchTargetMs } = getMSFromMSDict(pitch, {
      machineID: shot.machineID,
      ball_type: shot.ball_type,
    });

    if (
      checkHash &&
      shotTargetMs.matching_hash !== pitchTargetMs?.matching_hash
    ) {
      // reject/ignore shots (e.g. from discarded iterations while training)
      return;
    }

    // only set PT flag if the matching hash belongs to the pitch's current ms
    if (shotTargetMs.matching_hash === pitchTargetMs?.matching_hash) {
      shotTargetMs.precision_trained = pitchTargetMs?.precision_trained;
    }

    shotTargetMs.pitchBreaks = pitch.breaks;

    const o: IPitchPerformanceFire = {
      pitch: {
        id: shot.pitch_id,
        name: pitch.name ?? 'Unnamed Pitch',
        type: pitch.type,
      },
      shot: shot,
    };

    return o;
  };
}
